import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import { formatLatexPreview } from "../../../../components/inputs/FractionInput";

function randInt(min, max, exclude = []) {
  let n;
  do {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(n));
  return n;
}

function gcd(a, b) {
  return b === 0 ? Math.abs(a) : gcd(b, a % b);
}

function simplifyFraction(numerator, denominator) {
  const sign = Math.sign(numerator) * Math.sign(denominator || 1) || 1;
  const absNum = Math.abs(numerator);
  const absDen = Math.abs(denominator);
  const d = gcd(absNum, absDen);
  return {
    numerator: sign * (absNum / d),
    denominator: absDen / d,
  };
}

function fractionLatex(numerator, denominator) {
  if (denominator === 1) return `${numerator}`;
  if (numerator < 0) return `-\\dfrac{${Math.abs(numerator)}}{${denominator}}`;
  return `\\dfrac{${numerator}}{${denominator}}`;
}

function formatLinearLatex(aNumerator, aDenominator, b) {
  const aLatex = fractionLatex(aNumerator, aDenominator);
  const ax = aDenominator === 1 && aNumerator === 1
    ? "x"
    : aDenominator === 1 && aNumerator === -1
    ? "-x"
    : `${aLatex}x`;
  if (b === 0) return ax;
  return b > 0 ? `${ax} + ${b}` : `${ax} - ${Math.abs(b)}`;
}

export function generateTask() {
  const useFraction = Math.random() < 0.5;
  let aNumerator;
  let aDenominator;

  if (useFraction) {
    do {
      aNumerator = randInt(-9, 9, [0]);
      aDenominator = randInt(2, 6);
      ({ numerator: aNumerator, denominator: aDenominator } = simplifyFraction(aNumerator, aDenominator));
    } while (aDenominator === 1);
  } else {
    aNumerator = randInt(-8, 8, [0]);
    aDenominator = 1;
  }

  const b = randInt(-9, 9);
  const functionLatex = formatLinearLatex(aNumerator, aDenominator, b);
  const derivativeLatex = fractionLatex(aNumerator, aDenominator);
  const aValue = aNumerator / aDenominator;

  return {
    type: "LinearDerivativeTask",
    prompt: `Oblicz pochodna funkcji: f(x) = ${functionLatex}. Podaj f'(x).`,
    answer: aValue,
    data: {
      aNumerator,
      aDenominator,
      aValue,
      b,
      functionLatex,
      derivativeLatex,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Oblicz pochodna funkcji:</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <p>Podaj <InlineMath math={"f'(x)"} />.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="f'(x)"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const parsed = userAnswer.parsed;
  return typeof parsed === "number" && Math.abs(parsed - data.aValue) < 0.0001;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  return <InlineMath math={formatLatexPreview(userAnswer.raw, "f'(x)")} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`f'(x) = ${data.derivativeLatex}`} />;
}

export function renderExplanation({ data }) {
  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1:</strong> Dla funkcji liniowej <InlineMath math={"f(x)=ax+b"} />{" "}
        pochodna jest rowna wspolczynnikowi kierunkowemu.
      </p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <p>
        <strong>Krok 2:</strong> Wspolczynnik przy <InlineMath math="x" /> to{" "}
        <InlineMath math={data.derivativeLatex} />.
      </p>
      <BlockMath math={`f'(x) = ${data.derivativeLatex}`} />
    </div>
  );
}
